import { Injectable, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { AccountService, SubscriptionType } from '../account/account.service';
import { StackedModalsService } from '../modal/stacked-modals.service';
import { AccountDialogComponent } from '../../components/modals/account-dialog/account-dialog.component';
import { SubscriptionSuccessDialogComponent } from '../../components/modals/subscriptions-dialog/subscription-success-dialog/subscription-success-dialog.component';
import { LanguageService } from '../account/language.service';
import { ConfirmationDialogComponent } from 'src/app/components/modals/confirmation-dialog/confirmation-dialog.component';

/**
 * Owns account-driven dialog subscriptions:
 *   - ObservableOpenEditProfileDialog  → opens profile-edit modal
 *   - ObservableVoucherInfoDialog      → opens voucher-info modal
 *   - ObservableSubscriptionChangedDialog → opens subscription-success modal
 *
 * The ObservableIsAuthorized subscription previously lived here to sync a
 * MainComponent.isAuthorized field, but that field was unused in the template
 * and has been removed — any component that needs auth state injects
 * AccountService directly.
 *
 * Split from MainComponent because these subscriptions are fully self-contained
 * and require no ViewChildren or component-specific state.
 */
@Injectable({ providedIn: 'root' })
export class AccountFlowHandler {
  private editProfileSub: Subscription | null = null;
  private voucherSub: Subscription | null = null;
  private subscriptionChangedSub: Subscription | null = null;

  private readonly accountService = inject(AccountService);
  private readonly stackedModalsService = inject(StackedModalsService);
  private readonly languageService = inject(LanguageService);

  bind(): void {
    if (!this.editProfileSub) {
      this.editProfileSub = this.accountService.ObservableOpenEditProfileDialog.subscribe(resp => {
        if (resp != null && resp != undefined) {
          this.stackedModalsService.openModalWindow(AccountDialogComponent, {
            data: { process: 'profileEdit' },
            autoFocus: false,
            panelClass: 'dialog-snap-to-fullscreen',
          });
        }
      });
    }

    if (!this.voucherSub) {
      this.voucherSub = this.accountService.ObservableVoucherInfoDialog.subscribe(resp => {
        if (resp) {
          let message = "";
          if (resp.success) {
            message = this.languageService.languageJSON[resp.success];
          }
          else if (resp.error) {
            message = this.languageService.languageJSON[resp.error];
          }
          else {
            message = this.languageService.languageJSON.VOUC_ERR_012;
          }

          this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
            autoFocus: false,
            data: {
              title: this.languageService.languageJSON.VoucherDialog_Title, message: message,
              buttons: [this.languageService.languageJSON.Global_Ok, null]
            },
          });
        }
      });
    }

    if (!this.subscriptionChangedSub) {
      this.subscriptionChangedSub = this.accountService.ObservableSubscriptionChangedDialog.subscribe(resp => {
        if (resp) {
          this.stackedModalsService.openModalWindow(SubscriptionSuccessDialogComponent, {
            data: { subscriptionType: resp as SubscriptionType },
            autoFocus: false,
          });
        }
      });
    }
  }

  unbind(): void {
    this.editProfileSub?.unsubscribe();
    this.editProfileSub = null;
    this.voucherSub?.unsubscribe();
    this.voucherSub = null;
    this.subscriptionChangedSub?.unsubscribe();
    this.subscriptionChangedSub = null;
  }
}
